import { Fragment, useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import {
  Loader2,
  ChevronRight,
  Library,
} from 'lucide-react';
import { useAuth } from '@/features/auth';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { supabase } from '@/lib/supabase';
import { SSWCircle, BabyNameEntry } from '@/components/SSWCircle';
import { isNative } from '@/lib/capacitor';
import { ActiveContractionCard } from '@/features/contraction-timer/components';
import type { BlogPostWithCategory, BlogCategory } from '@/features/blog';
import { useAppMode } from '@/hooks/useAppMode';
import { BabyModeDashboard } from '@/components/dashboard/BabyModeDashboard';
import { ActivityFeedWidget } from '@/features/activity-feed';
import { isWissenswertesEnabled } from '@/services/systemSettings';
import { RecommendationSlot, useRandomInsertionIndex } from '@/features/recommendations';
import { useTranslation, useLocale } from '@parentpath/i18n';

// Helper to get the appropriate greeting based on time of day
function getTimeSlot(): string {
  const hour = new Date().getHours();
  if (hour >= 0 && hour < 6) return '00-06';
  if (hour >= 6 && hour < 11) return '06-11';
  if (hour >= 11 && hour < 13) return '11-13';
  if (hour >= 13 && hour < 18) return '13-18';
  return '18-00';
}

// Helper to parse baby_names (handles both old string[] and new BabyNameEntry[] format)
const parseBabyNames = (names: unknown): BabyNameEntry[] => {
  if (!names || !Array.isArray(names) || names.length === 0) return [];

  // Check if it's the old format (array of strings)
  if (typeof names[0] === 'string') {
    return names.map((name: string) => ({ name, gender: 'neutral' as const }));
  }

  // New format (array of objects)
  return names as BabyNameEntry[];
};

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentPregnancy, currentSSW, isLoading, setPregnancy, setLoading } = usePregnancyStore();
  const { appMode, lastCompletedPregnancy, isLoading: appModeLoading } = useAppMode();
  const { t, i18n } = useTranslation('dashboard');
  const { locale } = useLocale();
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [greeting, setGreeting] = useState<string>('');
  const [upcomingAppointments, setUpcomingAppointments] = useState<Array<{
    id: string;
    title: string;
    appointment_date: string;
    appointment_time: string | null;
    appointment_type: string;
  }>>([]);

  // Blog posts with load more functionality
  const [allBlogPosts, setAllBlogPosts] = useState<BlogPostWithCategory[]>([]);
  const [displayedPostsCount, setDisplayedPostsCount] = useState(5);
  const [blogLoading, setBlogLoading] = useState(true);
  const [showWissenswertes, setShowWissenswertes] = useState(false); // Default to false until setting is loaded
  const POSTS_PER_PAGE = 5;

  // Derived state for displayed posts
  const blogPosts = allBlogPosts.slice(0, displayedPostsCount);
  const allBlogPostsLoaded = displayedPostsCount >= allBlogPosts.length;
  // Random injection index for the product recommendation among the blog posts
  const wissenswertesRecIndex = useRandomInsertionIndex('wissenswertes_pregnancy', blogPosts.length);

  // Fetch blog posts with RPC
  const fetchBlogPosts = useCallback(async () => {
    if (!currentSSW) return;

    setBlogLoading(true);

    try {
      // Use the RPC function to get SSW-relevant posts (fetch all, paginate client-side)
      const { data: postsData, error: postsError } = await supabase.rpc('get_blog_posts_for_ssw', {
        p_ssw: currentSSW,
        p_is_risk_pregnancy: currentPregnancy?.is_high_risk || false,
        p_child_count: currentPregnancy?.child_count || 1,
        p_limit: 50, // Fetch more posts for client-side pagination
        p_locale: locale,
      });

      if (postsError) throw postsError;

      if (!postsData || postsData.length === 0) {
        setAllBlogPosts([]);
        return;
      }

      // Fetch categories for the posts
      const categoryIds = [...new Set(postsData.map((p: BlogPostWithCategory) => p.category_id).filter(Boolean))];
      let categoriesMap: Record<string, BlogCategory> = {};

      if (categoryIds.length > 0) {
        const { data: categoriesData } = await supabase
          .from('blog_categories')
          .select('*')
          .eq('locale', locale)
          .in('id', categoryIds);

        if (categoriesData) {
          categoriesMap = categoriesData.reduce((acc: Record<string, BlogCategory>, cat: BlogCategory) => {
            acc[cat.id] = cat;
            return acc;
          }, {});
        }
      }

      // Combine posts with their categories
      const postsWithCategories: BlogPostWithCategory[] = postsData.map((post: BlogPostWithCategory) => ({
        ...post,
        category: post.category_id ? categoriesMap[post.category_id] || null : null,
      }));

      setAllBlogPosts(postsWithCategories);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
    } finally {
      setBlogLoading(false);
    }
  }, [currentSSW, currentPregnancy?.is_high_risk, currentPregnancy?.child_count, locale]);

  // Initial blog posts fetch
  useEffect(() => {
    if (currentSSW) {
      setAllBlogPosts([]);
      setDisplayedPostsCount(5);
      fetchBlogPosts();
    }
  }, [currentSSW, fetchBlogPosts]);

  // Fetch Wissenswertes visibility setting
  useEffect(() => {
    isWissenswertesEnabled().then(setShowWissenswertes);
  }, []);

  const handleLoadMorePosts = () => {
    setDisplayedPostsCount(prev => prev + POSTS_PER_PAGE);
  };

  // Fetch greeting from database (locale-aware)
  useEffect(() => {
    async function fetchGreeting() {
      const timeSlot = getTimeSlot();
      // Try user's locale first, then fall back to 'de'
      const { data } = await supabase
        .from('dashboard_greetings')
        .select('greeting_text')
        .eq('time_slot', timeSlot)
        .eq('locale', locale)
        .maybeSingle();

      if (data?.greeting_text) {
        setGreeting(data.greeting_text);
      } else {
        // Default fallback greetings per locale
        const defaults: Record<string, Record<string, string>> = {
          de: {
            '00-06': 'Gute Nacht, {name}',
            '06-11': 'Guten Morgen, {name}',
            '11-13': 'Mahlzeit, {name}',
            '13-18': 'Guten Tag, {name}',
            '18-00': 'Guten Abend, {name}',
          },
          en: {
            '00-06': 'Good night, {name}',
            '06-11': 'Good morning, {name}',
            '11-13': 'Enjoy your lunch, {name}',
            '13-18': 'Good afternoon, {name}',
            '18-00': 'Good evening, {name}',
          },
        };
        const localeDefaults = defaults[locale] || defaults.de;
        setGreeting(localeDefaults[timeSlot] || 'Hallo, {name}');
      }
    }

    fetchGreeting();
  }, [locale]);

  // Fetch pregnancy data on mount
  useEffect(() => {
    async function fetchPregnancy() {
      if (!user) return;

      setLoading(true);
      setFetchError(null);
      try {
        // First, try to get pregnancy created by user
        let pregnancy = null;

        const { data: ownPregnancies, error: ownError } = await supabase
          .from('pregnancies')
          .select('*')
          .eq('created_by', user.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1);

        if (ownError) {
          console.error('Error fetching own pregnancy:', ownError);
          setFetchError(ownError.message);
          setLoading(false);
          return;
        }

        pregnancy = ownPregnancies?.[0] || null;

        // If no own pregnancy, check if user is a partner in an active connection
        if (!pregnancy) {
          const { data: partnerConnection } = await supabase
            .from('partner_connections')
            .select('pregnancy_id')
            .eq('user_2_id', user.id)
            .eq('status', 'active')
            .maybeSingle();

          if (partnerConnection?.pregnancy_id) {
            const { data: sharedPregnancy } = await supabase
              .from('pregnancies')
              .select('*')
              .eq('id', partnerConnection.pregnancy_id)
              .eq('is_active', true)
              .maybeSingle();

            pregnancy = sharedPregnancy || null;
          }
        }

        setPregnancy(pregnancy);

        // Fetch upcoming appointments
        if (pregnancy) {
          try {
            const today = new Date().toISOString().split('T')[0];
            const { data: appointments } = await supabase
              .from('user_appointments')
              .select('id, title, appointment_date, appointment_time, appointment_type')
              .eq('pregnancy_id', pregnancy.id)
              .gte('appointment_date', today)
              .order('appointment_date', { ascending: true })
              .limit(3);

            if (appointments) {
              setUpcomingAppointments(appointments);
            }
          } catch {
            // Appointments table might not exist yet, ignore
          }
        }
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchPregnancy();
  }, [user, setPregnancy, setLoading]);

  // NOTE: Onboarding redirect removed - now using Idle Dashboards instead

  // Calculate current day within the week (0-6)
  const dayInWeek = (() => {
    if (!currentPregnancy) return 0;
    const due = new Date(currentPregnancy.expected_due_date);
    const now = new Date();
    const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    const totalDays = 280 - diffDays;
    return totalDays % 7;
  })();

  // Parse baby names
  const babyNames = parseBabyNames(currentPregnancy?.baby_names);

  // Get user's first name for greeting
  const [firstName, setFirstName] = useState<string>('');
  useEffect(() => {
    async function fetchFirstName() {
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('first_name, display_name')
        .eq('id', user.id)
        .maybeSingle();
      // Use first_name if available, otherwise extract from display_name or use empty
      const name = data?.first_name || data?.display_name?.split(' ')[0] || '';
      setFirstName(name);
    }
    fetchFirstName();
  }, [user]);

  // Format greeting with first name only
  const formattedGreeting = greeting.replace('{name}', firstName || t('greetings.you'));

  if (isLoading || appModeLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  // Show error if fetch failed
  if (fetchError) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <h3 className="font-semibold text-red-800 mb-2">{t('error.title')}</h3>
          <p className="text-red-600 text-sm mb-4">{fetchError}</p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => window.location.reload()}
            >
              {t('error.retry')}
            </Button>
            <Link to="/onboarding">
              <Button variant="ghost" size="sm">
                {t('error.toOnboarding')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Redirect free users without pregnancy to onboarding
  // The app requires a pregnancy or a child (after birth) to function
  if (appMode === 'idle') {
    navigate('/onboarding');
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  // Render Baby Mode Dashboard for premium users after birth
  // Only show if they actually had a birth (have a child)
  if (appMode === 'baby') {
    const isBabyMode = lastCompletedPregnancy?.completion_type === 'birth';

    // If no birth (ended without baby), redirect to onboarding
    if (!isBabyMode) {
      navigate('/onboarding');
      return (
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      );
    }

    return (
      <>
        {!isNative && (
          <div className="fixed top-4 left-4 z-50">
            <Link to="/">
              <img
                src="https://parentpath.de/pp_logo-piktogram-w100.svg"
                alt="ParentPath"
                className="h-10 w-auto"
              />
            </Link>
          </div>
        )}
        <BabyModeDashboard lastCompletedPregnancy={lastCompletedPregnancy} />
      </>
    );
  }

  // If in pregnancy mode but no pregnancy exists, redirect to onboarding
  // This handles the case where a user has never created a pregnancy
  if (appMode === 'pregnancy' && !currentPregnancy) {
    navigate('/onboarding');
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  // Safety check: if somehow we have no pregnancy and unknown mode
  if (!currentPregnancy) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <>
      {/* Logo - only on home screen, only for web (native has it in TopBar) */}
      {!isNative && (
        <div className="fixed top-4 left-4 z-50">
          <Link to="/">
            <img
              src="https://parentpath.de/pp_logo-piktogram-w100.svg"
              alt="ParentPath"
              className="h-10 w-auto"
            />
          </Link>
        </div>
      )}

      {/* Active Contraction Card */}
      <div className="py-4">
        <ActiveContractionCard />
      </div>

      {/* SSW Section */}
      <section className="flex flex-col">
        {/* Greeting - left aligned */}
        <h1 className="text-white text-3xl font-bold text-left mb-6">
          {formattedGreeting}
        </h1>

        {/* SSW Circle - centered */}
        <div className="flex justify-center">
          <SSWCircle
            currentSSW={currentSSW || 1}
            dayInWeek={dayInWeek}
            babyNames={babyNames}
          />
        </div>

      </section>

      {/* Anstehende Termine Section */}
      <section className="mt-16">
        <h2 className="text-white text-2xl font-bold mb-4">{t('appointments.title')}</h2>

        <div className="space-y-5">
          {upcomingAppointments.length > 0 ? (
            <>
              {upcomingAppointments.map((apt) => {
                const aptDate = new Date(apt.appointment_date);
                const locale = i18n.language === 'en' ? 'en-GB' : 'de-DE';
                const day = aptDate.toLocaleDateString(locale, { day: 'numeric' });
                const month = aptDate.toLocaleDateString(locale, { month: 'short' }).toUpperCase().replace('.', '');
                const weekday = aptDate.toLocaleDateString(locale, { weekday: 'long' });
                const time = apt.appointment_time ? t('appointments.atTime', { time: apt.appointment_time }) : '';

                return (
                  <div key={apt.id} className="flex items-center gap-4">
                    <div className="date-box">
                      <span className="date-day text-white">{day}</span>
                      <span className="date-month text-white">{month}</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">{apt.title}</p>
                      <p className="text-white/70 text-sm">{t('appointments.onDay', { weekday, time })}</p>
                    </div>
                  </div>
                );
              })}

              {/* Alle anzeigen Button */}
              <Link to="/calendar" className="block mt-5">
                <button className="w-full py-3 bg-pp-orange rounded-pp flex items-center justify-center gap-2 text-white font-semibold text-base">
                  {t('appointments.showAll')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </Link>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-white/70 mb-4">{t('appointments.none')}</p>
              <Link to="/calendar">
                <button className="px-6 py-3 bg-pp-orange rounded-pp text-white font-medium">
                  {t('appointments.add')}
                </button>
              </Link>
            </div>
          )}
        </div>
      </section>


      {/* Activity Feed Widget */}
      <ActivityFeedWidget />

      {/* Wissenswertes Section - conditionally rendered based on admin setting */}
      {showWissenswertes && (
        <section className="mt-16">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Library className="w-8 h-8 text-white" />
              <h2 className="text-white text-2xl font-medium">{t('blog.title')}</h2>
            </div>
            <Link
              to="/blog"
              className="px-3 py-1.5 bg-pp-orange rounded-[5px] text-white text-sm font-medium"
            >
              {t('blog.showAll')}
            </Link>
          </div>

          {/* Blog Entries */}
          <div className="space-y-4">
            {blogLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-white" />
              </div>
            ) : blogPosts.length > 0 ? (
              <>
                {/* Start position */}
                <RecommendationSlot placement="wissenswertes_pregnancy" position="start" />
                {blogPosts.map((post, idx) => (
                  <Fragment key={post.id}>
                    {idx === wissenswertesRecIndex && (
                      <RecommendationSlot placement="wissenswertes_pregnancy" position="random" />
                    )}
                <Link
                  to={`/blog/${post.slug}`}
                  className="block glass-effect rounded-[15px] overflow-hidden hover:bg-white/15 transition-all"
                >
                  {post.image_url && (
                    <div className="aspect-video w-full overflow-hidden">
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {post.category?.name && (
                        <span className="inline-block text-xs font-medium px-2 py-0.5 bg-pp-teal border border-white/25 rounded-[5px] text-white">
                          {post.category.name}
                        </span>
                      )}
                      {post.ssw_min && post.ssw_max && (
                        <span className="inline-block text-xs font-medium px-2 py-0.5 bg-pp-teal border border-white/25 rounded-[5px] text-white">
                          SSW {post.ssw_min}-{post.ssw_max}
                        </span>
                      )}
                    </div>
                    <h2 className="font-semibold text-white mb-1">{post.title}</h2>
                    <div className="flex items-center justify-end mt-3">
                      <ChevronRight className="w-4 h-4 text-pp-orange" />
                    </div>
                  </div>
                </Link>
                  </Fragment>
                ))}
                {wissenswertesRecIndex === blogPosts.length && (
                  <RecommendationSlot placement="wissenswertes_pregnancy" position="random" />
                )}
                {/* End position */}
                <RecommendationSlot placement="wissenswertes_pregnancy" position="end" />
              </>
            ) : (
              <div className="glass-effect rounded-[15px] p-6 text-center text-white/60">
                <p>{t('blog.noArticles')}</p>
              </div>
            )}
          </div>

          {/* Load More / All Loaded */}
          {blogPosts.length > 0 && (
            <div className="mt-6">
              {allBlogPostsLoaded ? (
                <div className="text-center">
                  <p className="text-white/60 text-sm mb-3">
                    {t('blog.allLoaded')}
                  </p>
                </div>
              ) : (
                <button
                  onClick={handleLoadMorePosts}
                  className="mx-auto block px-8 py-3 bg-pp-orange rounded-pp text-white text-sm font-semibold"
                >
                  {t('blog.loadMore')}
                </button>
              )}
            </div>
          )}
        </section>
      )}
    </>
  );
}
